import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  ChevronLeft,
  Save,
  MessageSquare,
  Files,
  ClipboardEdit,
  Upload,
  Trash2,
  File,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Box,
  Typography,
  Chip,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import toast from "react-hot-toast";

// Import des vraies routes API
import {
  updateComment,
  addFiles,
  removeFileFromProblem,
  addFilesToExercise,
} from "server/Exercises CRUD/Requests";
import {
  UpdateCommentInput,
  AddFilesInput,
} from "server/Exercises CRUD/InputsTypes";
import { ApiResponse } from "server";

// Import pour récupérer les détails de la soumission
import { getSubmissionById } from "server/StartupAdmins CRUD/Requests";
import { GetSubmissionsOutput } from "server/StartupAdmins CRUD/OutputsTypes";
import { getFileDetails } from "server/Files CRUD/Requests";
import { GetFileDetailsOutput } from "server/Files CRUD/OutputsTypes";

interface EditModalProps {
  open: boolean;
  handleOpen: () => void;
  onSave: (updatedDetails: { comment?: string }) => void;
  submissionId: string; // Maintenant obligatoire
}

const steps = [
  {
    id: "comment",
    title: "Commentaire",
    icon: MessageSquare,
    description: "Modifier le commentaire",
  },
  {
    id: "files",
    title: "Fichiers",
    icon: Files,
    description: "Gérer les fichiers",
  },
];

export const EditModal: React.FC<EditModalProps> = ({
  open,
  handleOpen,
  onSave,
  submissionId,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [comment, setComment] = useState("");

  // États pour la soumission et les fichiers
  const [submission, setSubmission] = useState<GetSubmissionsOutput | null>(
    null,
  );
  const [loadingSubmission, setLoadingSubmission] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  const [subjectFiles, setSubjectFiles] = useState<GetFileDetailsOutput[]>([]);
  const [productionFiles, setProductionFiles] = useState<
    GetFileDetailsOutput[]
  >([]);
  const [loadingFiles, setLoadingFiles] = useState(false);

  // États pour les opérations
  const [updatingComment, setUpdatingComment] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [removingFile, setRemovingFile] = useState<string | null>(null);

  // File input refs
  const subjectFileInputRef = useRef<HTMLInputElement>(null);
  const productionFileInputRef = useRef<HTMLInputElement>(null);

  // Récupérer la soumission au chargement
  useEffect(() => {
    if (open && submissionId) {
      fetchSubmission();
    }
  }, [open, submissionId]);

  const fetchSubmission = async () => {
    setLoadingSubmission(true);
    setSubmissionError(null);

    try {
      const response = await getSubmissionById(submissionId);

      if (response.status === 200) {
        const submissionData = response.body as GetSubmissionsOutput;
        setSubmission(submissionData);

        // Mettre à jour le commentaire
        setComment(submissionData.comment || "");

        // Charger les fichiers
        await loadFilesMetadata(submissionData);
      } else {
        setSubmissionError("Impossible de récupérer la soumission");
      }
    } catch (error) {
      setSubmissionError("Erreur lors de la récupération de la soumission");
    } finally {
      setLoadingSubmission(false);
    }
  };

  const loadFilesMetadata = async (submissionData: GetSubmissionsOutput) => {
    setLoadingFiles(true);

    try {
      // Charger les métadonnées des fichiers de sujet
      if (
        submissionData.subjectFileIds?.length &&
        submissionData.subjectFileIds.length > 0
      ) {
        const subjectFilesData = (
          await Promise.allSettled(
            submissionData.subjectFileIds.map(async (fileId) => {
              try {
                const response = await getFileDetails(fileId);
                if (response.status === 200) {
                  return response.body as GetFileDetailsOutput;
                }
                return null;
              } catch (error) {
                console.error(`Erreur sur le fichier ${fileId}:`, error);
                return null;
              }
            }),
          )
        )
          .filter(
            (
              result,
            ): result is PromiseFulfilledResult<GetFileDetailsOutput | null> =>
              result.status === "fulfilled" && !!result.value,
          )
          .map((result) => result.value!);

        setSubjectFiles(subjectFilesData.filter(Boolean));
      } else {
        setSubjectFiles([]);
      }

      // Charger les métadonnées des fichiers de production
      if (
        submissionData.productionFileIds?.length &&
        submissionData.productionFileIds.length > 0
      ) {
        const productionFilesData = (
          await Promise.allSettled(
            submissionData.productionFileIds.map(async (fileId) => {
              try {
                const response = await getFileDetails(fileId);
                if (response.status === 200) {
                  return response.body as GetFileDetailsOutput;
                }
                return null;
              } catch (error) {
                console.error(`Erreur sur le fichier ${fileId}:`, error);
                return null;
              }
            }),
          )
        )
          .filter(
            (
              result,
            ): result is PromiseFulfilledResult<GetFileDetailsOutput | null> =>
              result.status === "fulfilled" && !!result.value,
          )
          .map((result) => result.value!);

        setProductionFiles(productionFilesData.filter(Boolean));
      } else {
        setProductionFiles([]);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des fichiers:", error);
    } finally {
      setLoadingFiles(false);
    }
  };

  // Supprimer un fichier
  const handleRemoveFile = async (
    fileId: string,
    fileType: "subject" | "production",
  ) => {
    if (!submission) return;

    setRemovingFile(fileId);

    try {
      const response = await removeFileFromProblem(submissionId, fileId);

      if (response.status === 200) {
        toast.success("Fichier supprimé avec succès !");

        // Mettre à jour immédiatement les listes locales
        if (fileType === "subject") {
          setSubjectFiles((prev) => prev.filter((file) => file._id !== fileId));
        } else {
          setProductionFiles((prev) =>
            prev.filter((file) => file._id !== fileId),
          );
        }
      } else {
        toast.error("Erreur lors de la suppression du fichier");
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression du fichier");
    } finally {
      setRemovingFile(null);
    }
  };

  // Ajouter des fichiers
  const handleFileUpload = async (
    type: "subject" | "production",
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !submission) return;

    setUploadingFiles(true);

    try {
      const formData = new FormData();

      if (type === "subject") {
        Array.from(files).forEach((file) => {
          formData.append("subjectFileIds", file);
        });
      } else {
        Array.from(files).forEach((file) => {
          formData.append("productionFileIds", file);
        });
      }

      const response = await addFilesToExercise(submissionId, formData);

      if (response.status === 200) {
        toast.success(
          `Fichiers ${
            type === "subject" ? "de sujet" : "de production"
          } ajoutés avec succès !`,
        );

        // Recharger la soumission pour avoir les nouveaux IDs
        await fetchSubmission();
      } else {
        toast.error("Erreur lors de l'ajout des fichiers");
      }
    } catch (error) {
      toast.error("Erreur lors de l'ajout des fichiers");
    } finally {
      setUploadingFiles(false);

      // Reset file input
      if (type === "subject" && subjectFileInputRef.current) {
        subjectFileInputRef.current.value = "";
      } else if (type === "production" && productionFileInputRef.current) {
        productionFileInputRef.current.value = "";
      }
    }
  };

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  // Mise à jour du commentaire
  const handleUpdateComment = async () => {
    if (!comment.trim()) {
      toast.error("Le commentaire ne peut pas être vide");
      return;
    }

    setUpdatingComment(true);
    try {
      const input: UpdateCommentInput = {
        comment: comment,
      };

      const response: ApiResponse<any> = await updateComment(
        submissionId,
        input,
      );

      if (response.status === 200) {
        toast.success("Commentaire mis à jour avec succès !");
        onSave({ comment: comment });
      } else {
        throw new Error(
          (response.body as any)?.message || "Erreur lors de la mise à jour",
        );
      }
    } catch (error) {
      console.error("Erreur lors de la mise à jour du commentaire:", error);
      toast.error("Erreur lors de la mise à jour du commentaire");
    } finally {
      setUpdatingComment(false);
    }
  };

  const handleSubmit = () => {
    // Sauvegarder les modifications selon l'étape actuelle
    switch (activeStep) {
      case 0: // Étape commentaire
        handleUpdateComment();
        break;
      case 1: // Étape fichiers
        // Les fichiers sont déjà gérés individuellement
        toast.success("Modifications sauvegardées !");
        break;
      default:
        onSave({ comment: comment });
        handleOpen();
    }
  };

  const triggerFileInput = (type: "subject" | "production") => {
    if (type === "subject" && subjectFileInputRef.current) {
      subjectFileInputRef.current.click();
    } else if (type === "production" && productionFileInputRef.current) {
      productionFileInputRef.current.click();
    }
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  // Affichage des erreurs
  if (submissionError) {
    return (
      <Dialog open={open} onClose={handleOpen} maxWidth="sm" fullWidth>
        <DialogContent sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" color="error" sx={{ mb: 2 }}>
            Erreur
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {submissionError}
          </Typography>
          <Button onClick={handleOpen} variant="contained">
            Fermer
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  const renderStepContent = (step: number) => {
    const variants = {
      enter: (direction: number) => ({
        x: direction > 0 ? 1000 : -1000,
        opacity: 0,
      }),
      center: {
        zIndex: 1,
        x: 0,
        opacity: 1,
      },
      exit: (direction: number) => ({
        zIndex: 0,
        x: direction < 0 ? 1000 : -1000,
        opacity: 0,
      }),
    };

    const swipeConfidenceThreshold = 10000;
    const swipePower = (offset: number, velocity: number) => {
      return Math.abs(offset) * velocity;
    };

    const paginate = (newDirection: number) => {
      if (newDirection === 1 && activeStep < steps.length - 1) {
        handleNext();
      } else if (newDirection === -1 && activeStep > 0) {
        handleBack();
      }
    };

    return (
      <motion.div
        key={step}
        custom={step}
        variants={variants}
        initial="enter"
        animate="center"
        exit="exit"
        transition={{
          x: { type: "spring", stiffness: 300, damping: 30 },
          opacity: { duration: 0.2 },
        }}
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={1}
        onDragEnd={(e, { offset, velocity }) => {
          const swipe = swipePower(offset.x, velocity.x);

          if (swipe < -swipeConfidenceThreshold) {
            paginate(1);
          } else if (swipe > swipeConfidenceThreshold) {
            paginate(-1);
          }
        }}
        className="w-full"
      >
        {step === 0 && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Commentaire
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={6}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all resize-none"
                placeholder="Ajoutez un commentaire"
              />
            </div>

            <div className="flex justify-end">
              <Button
                variant="contained"
                onClick={handleUpdateComment}
                disabled={updatingComment || !comment.trim()}
                startIcon={
                  updatingComment ? (
                    <CircularProgress size={16} />
                  ) : (
                    <Save size={16} />
                  )
                }
                sx={{
                  bgcolor: "#f59e0b",
                  "&:hover": {
                    bgcolor: "#d97706",
                  },
                  "&:disabled": {
                    bgcolor: "#fbbf24",
                  },
                }}
              >
                {updatingComment
                  ? "Sauvegarde..."
                  : "Sauvegarder le commentaire"}
              </Button>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-6">
            {/* Loading indicator */}
            {loadingSubmission &&
              subjectFiles.length === 0 &&
              productionFiles.length === 0 && (
                <div className="flex justify-center items-center py-8">
                  <CircularProgress size={40} sx={{ color: "#f59e0b" }} />
                  <Typography
                    variant="body2"
                    sx={{ ml: 2, color: "text.secondary" }}
                  >
                    Chargement des fichiers existants...
                  </Typography>
                </div>
              )}

            {/* Hidden file inputs */}
            <input
              type="file"
              ref={subjectFileInputRef}
              onChange={(e) => handleFileUpload("subject", e)}
              className="hidden"
              multiple
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            />
            <input
              type="file"
              ref={productionFileInputRef}
              onChange={(e) => handleFileUpload("production", e)}
              className="hidden"
              multiple
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            />

            {/* Subject Files Section */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-medium text-gray-700">
                  Fichiers de sujet
                </h3>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={
                    uploadingFiles ? (
                      <CircularProgress size={16} />
                    ) : (
                      <Upload size={16} />
                    )
                  }
                  onClick={() => triggerFileInput("subject")}
                  disabled={uploadingFiles}
                  sx={{
                    borderColor: "#f59e0b",
                    color: "#f59e0b",
                    "&:hover": {
                      borderColor: "#d97706",
                      backgroundColor: "rgba(245, 158, 11, 0.04)",
                    },
                  }}
                >
                  {uploadingFiles ? "Téléversement..." : "Téléverser"}
                </Button>
              </div>

              {subjectFiles.length > 0 && (
                <div className="mb-4">
                  <Typography
                    variant="caption"
                    sx={{ display: "block", mb: 1, color: "text.secondary" }}
                  >
                    Fichiers de sujet existants
                  </Typography>
                  <div className="space-y-2">
                    {subjectFiles.map((file) => (
                      <div
                        key={file._id}
                        className="p-3 bg-gray-50 rounded-lg border border-gray-200 flex items-center gap-2"
                      >
                        <File size={16} className="text-gray-500" />
                        <div className="flex-1 min-w-0">
                          <Typography
                            variant="subtitle2"
                            noWrap
                            sx={{
                              maxWidth: "100%",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={
                              file.originalName ||
                              `Fichier ${file._id.slice(-6)}`
                            }
                          >
                            {file.originalName ||
                              `Fichier ${file._id.slice(-6)}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {file.mimeType} • {formatFileSize(file.size || 0)}
                          </Typography>
                        </div>
                        <Tooltip title="Supprimer définitivement">
                          <button
                            onClick={() =>
                              handleRemoveFile(file._id, "subject")
                            }
                            disabled={removingFile === file._id}
                            className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50 transition-colors disabled:opacity-50"
                          >
                            {removingFile === file._id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        </Tooltip>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {subjectFiles.length === 0 && (
                <div className="mb-4">
                  <Typography
                    variant="caption"
                    sx={{ display: "block", mb: 1, color: "text.secondary" }}
                  >
                    Aucun fichier de sujet existant
                  </Typography>
                </div>
              )}
            </div>

            {/* Production Files Section */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-medium text-gray-700">
                  Fichiers de production
                </h3>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={
                    uploadingFiles ? (
                      <CircularProgress size={16} />
                    ) : (
                      <Upload size={16} />
                    )
                  }
                  onClick={() => triggerFileInput("production")}
                  disabled={uploadingFiles}
                  sx={{
                    borderColor: "#f59e0b",
                    color: "#f59e0b",
                    "&:hover": {
                      borderColor: "#d97706",
                      backgroundColor: "rgba(245, 158, 11, 0.04)",
                    },
                  }}
                >
                  {uploadingFiles ? "Téléversement..." : "Téléverser"}
                </Button>
              </div>
              {productionFiles.length > 0 && (
                <div className="mb-4">
                  <Typography
                    variant="caption"
                    sx={{ display: "block", mb: 1, color: "text.secondary" }}
                  >
                    Fichiers de production existants
                  </Typography>
                  <div className="space-y-2">
                    {productionFiles.map((file) => (
                      <div
                        key={file._id}
                        className="p-3 bg-gray-50 rounded-lg border border-gray-200 flex items-center gap-2"
                      >
                        <File size={16} className="text-gray-500" />
                        <div className="flex-1 min-w-0">
                          <Typography
                            variant="subtitle2"
                            noWrap
                            sx={{
                              maxWidth: "100%",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={
                              file.originalName ||
                              `Fichier ${file._id.slice(-6)}`
                            }
                          >
                            {file.originalName ||
                              `Fichier ${file._id.slice(-6)}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {file.mimeType} • {formatFileSize(file.size || 0)}
                          </Typography>
                        </div>
                        <Tooltip title="Supprimer définitivement">
                          <button
                            onClick={() =>
                              handleRemoveFile(file._id, "production")
                            }
                            disabled={removingFile === file._id}
                            className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50 transition-colors disabled:opacity-50"
                          >
                            {removingFile === file._id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        </Tooltip>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {productionFiles.length === 0 && (
                <div className="mb-4">
                  <Typography
                    variant="caption"
                    sx={{ display: "block", mb: 1, color: "text.secondary" }}
                  >
                    Aucun fichier de production existant
                  </Typography>
                </div>
              )}
            </div>
          </div>
        )}
      </motion.div>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleOpen}
      maxWidth="lg"
      fullWidth
      sx={{ zIndex: 9999 }}
    >
      {/* Header */}
      <Box
        sx={{
          background: "#E3B505",
          padding: 3,
          color: "white",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontWeight: "bold",
            }}
          >
            <ClipboardEdit size={24} />
            Modifier la soumission
          </Typography>
          <IconButton onClick={handleOpen} sx={{ color: "white" }}>
            <X size={24} />
          </IconButton>
        </Box>

        {/* Progress Steps */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            position: "relative",
          }}
        >
          {/* Progress Line */}
          <Box
            sx={{
              position: "absolute",
              top: "20px",
              left: 0,
              width: "100%",
              height: "2px",
              bgcolor: "rgba(255, 255, 255, 0.3)",
            }}
          >
            <Box
              component={motion.div}
              initial={false}
              animate={{
                width: `${(activeStep / (steps.length - 1)) * 100}%`,
              }}
              sx={{ height: "100%", bgcolor: "white" }}
              transition={{ duration: 0.3 }}
            />
          </Box>

          {/* Steps */}
          {steps.map((step, index) => {
            const StepIcon = step.icon;
            const isActive = index <= activeStep;
            const isCurrent = index === activeStep;

            return (
              <Box
                key={step.id}
                sx={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  width: `${100 / steps.length}%`,
                }}
              >
                <Box
                  component={motion.button}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setActiveStep(index)}
                  sx={{
                    position: "relative",
                    zIndex: 10,
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s",
                    bgcolor: isActive ? "white" : "rgba(255, 255, 255, 0.3)",
                    color: isActive ? "#E3B505" : "rgba(255, 255, 255, 0.7)",
                    ...(isCurrent && {
                      boxShadow: "0 0 0 4px rgba(255, 255, 255, 0.3)",
                    }),
                  }}
                >
                  <StepIcon size={20} />
                </Box>

                <Box
                  component={motion.div}
                  initial={false}
                  animate={{
                    opacity: isActive ? 1 : 0.5,
                    y: isCurrent ? -4 : 0,
                  }}
                  sx={{ mt: 1.5, textAlign: "center" }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ color: "white", fontWeight: "medium" }}
                  >
                    {step.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "rgba(255, 255, 255, 0.8)", display: "block" }}
                  >
                    {step.description}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Content */}
      <DialogContent sx={{ p: 3 }}>
        <AnimatePresence initial={false}>
          {renderStepContent(activeStep)}
        </AnimatePresence>
      </DialogContent>

      {/* Footer */}
      <DialogActions
        sx={{
          borderTop: "1px solid rgba(0, 0, 0, 0.12)",
          px: 3,
          py: 2,
          bgcolor: "rgba(0, 0, 0, 0.03)",
          justifyContent: "space-between",
        }}
      >
        <Button
          onClick={handleBack}
          disabled={activeStep === 0}
          startIcon={<ChevronLeft size={20} />}
          sx={{
            color: activeStep === 0 ? "text.disabled" : "text.primary",
            "&:hover": {
              bgcolor: "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          Retour
        </Button>

        <Button
          onClick={handleOpen}
          variant="contained"
          startIcon={<X size={20} />}
          sx={{
            bgcolor: "#E3B505",
            "&:hover": {
              bgcolor: "#D1A305",
            },
          }}
        >
          Fermer
        </Button>
      </DialogActions>
    </Dialog>
  );
};
